import React, { useMemo, useState } from 'react';
import { getProgressionSkillsForSport } from '../data/progression/skills';
import { normalizeSportName } from '../utils/drillTemplates';

function TokenEditor({
  label,
  description,
  values = [],
  onChange,
  placeholder,
}) {
  const [draft, setDraft] = useState('');

  const addValue = () => {
    const nextValue = draft.trim();
    if (!nextValue) return;
    onChange(Array.from(new Set([...(values || []), nextValue])));
    setDraft('');
  };

  const removeValue = (valueToRemove) => {
    onChange((values || []).filter((value) => value !== valueToRemove));
  };

  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {(values || []).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => removeValue(value)}
            className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700 hover:bg-slate-200"
          >
            {value} ×
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addValue();
            }
          }}
        />
        <button
          type="button"
          onClick={addValue}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function StepsEditor({ steps = [], onChange }) {
  const nextSteps = steps?.length ? steps : [''];

  const updateStep = (index, value) => {
    onChange(nextSteps.map((step, stepIndex) => (stepIndex === index ? value : step)));
  };

  const addStep = () => {
    onChange([...(nextSteps || []), '']);
  };

  const removeStep = (index) => {
    const filtered = nextSteps.filter((_, stepIndex) => stepIndex !== index);
    onChange(filtered.length ? filtered : ['']);
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">Teaching Steps</p>
        <p className="mt-1 text-sm text-slate-600">Break the drill into clear coaching steps or progression stages.</p>
      </div>
      {nextSteps.map((step, index) => (
        <div key={`${index}-${step}`} className="flex gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
            {index + 1}
          </div>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder={`Step ${index + 1}`}
            value={step}
            onChange={(e) => updateStep(index, e.target.value)}
          />
          <button
            type="button"
            onClick={() => removeStep(index)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addStep}
        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        Add Step
      </button>
    </div>
  );
}

function RelatedSkillPicker({ sport = '', selectedSkillIds = [], onChange }) {
  const normalizedSport = normalizeSportName(sport);
  const availableSkills = useMemo(
    () => (normalizedSport ? getProgressionSkillsForSport(normalizedSport) : []),
    [normalizedSport]
  );

  const toggleSkill = (skillId) => {
    if ((selectedSkillIds || []).includes(skillId)) {
      onChange((selectedSkillIds || []).filter((value) => value !== skillId));
      return;
    }
    onChange([...(selectedSkillIds || []), skillId]);
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">Linked Skills</p>
        <p className="mt-1 text-sm text-slate-600">
          Link this drill to progression skills when that metadata exists for the selected sport.
        </p>
      </div>
      {availableSkills.length ? (
        <div className="grid gap-2 md:grid-cols-2">
          {availableSkills.map((skill) => {
            const selected = (selectedSkillIds || []).includes(skill.skillId);
            return (
              <button
                key={skill.skillId}
                type="button"
                onClick={() => toggleSkill(skill.skillId)}
                className={`rounded-2xl border px-3 py-3 text-left ${
                  selected
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <p className="text-sm font-semibold">{skill.name}</p>
                <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{skill.level}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          No structured skill browser is available for this sport yet. You can still save the drill without linked skills.
        </div>
      )}
    </div>
  );
}

function DrillTemplateEditorForm({
  form,
  onChange,
  ownerTypeOptions = [],
  accessiblePackOptions = [],
  memberships = [],
  selectedOrganizationId = '',
  onSelectedOrganizationIdChange,
  onSubmit,
  saving = false,
  mode = 'create',
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="text-lg font-semibold text-slate-950">
        {mode === 'edit' ? 'Edit Drill Template' : 'Create Drill Template'}
      </h2>

      {memberships.length > 1 && onSelectedOrganizationIdChange ? (
        <div className="mt-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Organization Context</label>
          <select
            value={selectedOrganizationId}
            onChange={(e) => onSelectedOrganizationIdChange(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            {memberships.map((membership) => (
              <option key={membership.organizationId} value={membership.organizationId}>
                {membership.organizationName}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="mt-4 space-y-6">
        <section className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Basics</p>
            <p className="mt-1 text-sm text-slate-600">Core teaching metadata and searchable labels.</p>
          </div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Title" value={form.title} onChange={(e) => onChange('title', e.target.value)} />
          <textarea className="min-h-[88px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Summary / description" value={form.summary} onChange={(e) => onChange('summary', e.target.value)} />
          <textarea className="min-h-[88px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Longer teaching description (optional)" value={form.description} onChange={(e) => onChange('description', e.target.value)} />
          <div className="grid gap-3 md:grid-cols-2">
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Sport" value={form.sport} onChange={(e) => onChange('sport', e.target.value)} />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Discipline" value={form.discipline} onChange={(e) => onChange('discipline', e.target.value)} />
          </div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Topic" value={form.topic} onChange={(e) => onChange('topic', e.target.value)} />
          <TokenEditor
            label="Tags"
            description="Use tags for search, categorization, and drill discovery."
            values={form.tags}
            onChange={(values) => onChange('tags', values)}
            placeholder="Add a tag"
          />
          <TokenEditor
            label="Focus Areas"
            description="Capture the key movement themes or coaching focuses."
            values={form.focusAreas}
            onChange={(values) => onChange('focusAreas', values)}
            placeholder="Add a focus area"
          />
          <TokenEditor
            label="Levels"
            description="Add the levels this drill is most appropriate for."
            values={form.levels}
            onChange={(values) => onChange('levels', values)}
            placeholder="Add a level"
          />
        </section>

        <section className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Teaching Detail</p>
            <p className="mt-1 text-sm text-slate-600">Corrections, cues, steps, and linked skill metadata.</p>
          </div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Common error" value={form.commonError} onChange={(e) => onChange('commonError', e.target.value)} />
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Correction cue" value={form.correctionCue} onChange={(e) => onChange('correctionCue', e.target.value)} />
          <StepsEditor steps={form.steps} onChange={(values) => onChange('steps', values)} />
          <RelatedSkillPicker
            sport={form.sport}
            selectedSkillIds={form.relatedSkillIds}
            onChange={(values) => onChange('relatedSkillIds', values)}
          />
        </section>

        <section className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Access & Ownership</p>
            <p className="mt-1 text-sm text-slate-600">Who owns this template, who can see it, and how premium access works.</p>
          </div>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={form.ownerType} onChange={(e) => onChange('ownerType', e.target.value)}>
            {ownerTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={form.visibility} onChange={(e) => onChange('visibility', e.target.value)}>
            <option value="private">Private</option>
            <option value="organization">Organization</option>
            <option value="assigned_only">Assigned only</option>
            <option value="platform">Platform</option>
          </select>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={form.accessTier} onChange={(e) => onChange('accessTier', e.target.value)}>
            <option value="free">Free</option>
            <option value="organization_included">Organization included</option>
            <option value="premium">Premium</option>
          </select>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={form.contentPackId} onChange={(e) => onChange('contentPackId', e.target.value)}>
            <option value="">No content pack</option>
            {accessiblePackOptions.map((contentPack) => (
              <option key={contentPack.contentPackId} value={contentPack.contentPackId}>{contentPack.name}</option>
            ))}
          </select>
          <TokenEditor
            label="Assigned Users"
            description="When visibility is assigned-only, these users can access the drill directly."
            values={form.assignedUserIds}
            onChange={(values) => onChange('assignedUserIds', values)}
            placeholder="Add a user ID"
          />
        </section>

        <button
          type="button"
          disabled={saving}
          onClick={onSubmit}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving ? 'Saving...' : mode === 'edit' ? 'Save Drill Template' : 'Create Drill Template'}
        </button>
      </div>
    </div>
  );
}

export default DrillTemplateEditorForm;
